import React from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type TreatmentsByBranchChartProps = {
    treatmentStats: {
        byBranch: Record<string, {todo: number, inProgress: number, completed: number}>
    };
    loading?: boolean;
};

export const TreatmentsByBranchChart: React.FC<TreatmentsByBranchChartProps> = ({ treatmentStats, loading }) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    // Get branch data
    const branches = Object.keys(treatmentStats.byBranch);
    const todoData = branches.map(branch => treatmentStats.byBranch[branch].todo);
    const inProgressData = branches.map(branch => treatmentStats.byBranch[branch].inProgress);
    const completedData = branches.map(branch => treatmentStats.byBranch[branch].completed);

    const chartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            fontFamily: 'inherit',
            toolbar: {
                show: false
            },
            stacked: true
        },
        plotOptions: {
            bar: {
                horizontal: false,
                borderRadius: 4,
                columnWidth: '60%'
            }
        },
        colors: ['#ffc700', '#009ef7', '#50cd89'], // Yellow (TODO), Blue (In Progress), Green (Completed)
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: branches,
            labels: {
                style: {
                    colors: isDarkMode ? '#ffffff' : '#181c32',
                    fontSize: '12px',
                    fontWeight: 600
                },
                rotate: -45,
                rotateAlways: branches.length > 3
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#a1a5b7',
                    fontSize: '12px'
                }
            }
        },
        grid: {
            borderColor: isDarkMode ? '#2b2b40' : '#eff2f5',
            strokeDashArray: 4,
            yaxis: {
                lines: {
                    show: true
                }
            }
        },
        tooltip: {
            theme: isDarkMode ? 'dark' : 'light',
            y: {
                formatter: function (val: number) {
                    return val + ' treatments'
                }
            }
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
            labels: {
                colors: isDarkMode ? '#ffffff' : '#181c32'
            }
        }
    };

    const series = [
        { name: 'TODO', data: todoData },
        { name: 'In Progress', data: inProgressData },
        { name: 'Completed', data: completedData }
    ];

    return (
        <div className="card h-100" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '' }}>
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className={`card-label fw-bolder fs-3 ${isDarkMode ? 'text-white' : 'text-dark'}`}>Treatments by Branch</span>
                    <span className="text-muted mt-1 fw-bold fs-7">Treatment status breakdown per branch</span>
                </h3>
            </div>
            <div className="card-body pt-3 pb-3">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '300px'}}>
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : branches.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center h-200px text-gray-400 fw-bold">
                        No treatment data available
                    </div>
                ) : (
                    <div id="treatments_branch_chart">
                        <Chart options={chartOptions} series={series} type="bar" height={300} />
                    </div>
                )}
            </div>
        </div>
    );
};
